import * as Immutable from 'immutable';
import type ProductOptionStore from '../../../../AppData/_stores/ProductOptionStore';
import type ProductStore from '../../../../AppData/_stores/ProductStore';
import type VariantPriceStore from '../../../../UserData/_stores/Store/VariantPriceStore';
import VariantPriceHelper from '../../../../AppData/VariantPriceHelper';
import type { StoreVariantOptionTypes } from './StoreVariantLimit';
import type PriceStore from '../../../../UIData/_stores/PriceStore';

export type StoreListingVariantPriceMap = Immutable.Map<string, VariantPriceStore> | Immutable.OrderedMap<string, VariantPriceStore>

interface DefaultPriceCache {
	disabledOptionIds: Immutable.List<number>
	prices: Immutable.OrderedMap<string, VariantPriceStore>
	allPrices?: Immutable.OrderedMap<string, VariantPriceStore>
}

export class StoreListingVariantPrices {
	private static readonly defaultPrices = new WeakMap<ProductStore, WeakMap<StoreVariantOptionTypes, DefaultPriceCache>>();

	private static readonly normalizedPrices = new WeakMap<StoreListingVariantPriceMap, WeakMap<StoreListingVariantPriceMap, StoreListingVariantPriceMap>>();

	static getChoiceKey(variantPrice: VariantPriceStore): string {
		// Keep dimensions already collapsed by VariantPriceHelper (e.g. free colors) in the same price group.
		const priceChoices = new Set(variantPrice.get('slug').split('|').map(choice => choice.split('$')[0]));
		return JSON.stringify(variantPrice.get('options').filter(option => priceChoices.has(option.get('slug')))
			.map(option => [option.get('id_product_option_type'), option.get('slug')] as const)
			.sort(([typeA], [typeB]) => typeA - typeB));
	}

	/** Saved prices take precedence over defaults; conflicting saved prices use the highest. */
	static normalizeOverrides<T extends StoreListingVariantPriceMap>(
		variantPrices: T,
		product: ProductStore,
		availableOptionTypes: StoreVariantOptionTypes,
	): T {
		const defaults = this.getDefaultPrices(product, availableOptionTypes, Immutable.List<number>());
		let cache = this.normalizedPrices.get(variantPrices);
		const cached = cache?.get(defaults);
		if (cached) return cached as T;

		const defaultPrices = new Map<string, { price: PriceStore, differs: boolean }>();
		const savedPrices = new Map<string, PriceStore>();
		defaults.forEach(variantPrice => {
			const key = this.getChoiceKey(variantPrice);
			const current = defaultPrices.get(key);
			if (!current) {
				defaultPrices.set(key, { price: variantPrice.get('price'), differs: false });
			} else {
				current.differs ||= !current.price.equals(variantPrice.get('price'));
				if (variantPrice.get('price').get('amount') > current.price.get('amount')) current.price = variantPrice.get('price');
			}
		});
		variantPrices.forEach((variantPrice, id) => {
			const metadata = defaults.get(id);
			if (!metadata) return;
			const key = this.getChoiceKey(metadata);
			const current = savedPrices.get(key);
			if (!current || variantPrice.get('price').get('amount') > current.get('amount')) savedPrices.set(key, variantPrice.get('price'));
		});

		const normalized = variantPrices.withMutations(prices => {
			defaults.forEach((variantPrice, id) => {
				const key = this.getChoiceKey(variantPrice);
				const price = savedPrices.get(key) ?? (defaultPrices.get(key)?.differs ? defaultPrices.get(key)?.price : undefined);
				if (price) prices.set(id, variantPrice.set('price', price));
			});
		}) as T;
		if (!cache) {
			cache = new WeakMap();
			this.normalizedPrices.set(variantPrices, cache);
		}
		cache.set(defaults, normalized);
		return normalized;
	}

	/** Keep disabled aliases in the draft too, so re-enabling them inherits the same price. */
	static setChoicePrice<T extends StoreListingVariantPriceMap>(
		variantPrices: T,
		choices: VariantPriceStore[],
		price: PriceStore,
		context: { product: ProductStore, availableOptionTypes: StoreVariantOptionTypes },
	): T {
		const keys = new Set(choices.map(choice => this.getChoiceKey(choice)));
		return this.normalizeOverrides(variantPrices, context.product, context.availableOptionTypes).withMutations(prices => {
			this.getDefaultPrices(context.product, context.availableOptionTypes, Immutable.List<number>()).forEach((variantPrice, id) => {
				if (keys.has(this.getChoiceKey(variantPrice))) prices.set(id, variantPrice.set('price', price));
			});
		}) as T;
	}

	/** Restore inherited pricing for every alias, including disabled draft combinations. */
	static resetChoicePrice<T extends StoreListingVariantPriceMap>(
		variantPrices: T,
		choices: VariantPriceStore[],
		context: { product: ProductStore, availableOptionTypes: StoreVariantOptionTypes },
	): T {
		const keys = new Set(choices.map(choice => this.getChoiceKey(choice)));
		return this.normalizeOverrides(variantPrices, context.product, context.availableOptionTypes).withMutations(prices => {
			this.getDefaultPrices(context.product, context.availableOptionTypes, Immutable.List<number>()).forEach((variantPrice, id) => {
				if (keys.has(this.getChoiceKey(variantPrice))) prices.remove(id);
			});
		}) as T;
	}

	/** Reuse the generated matrix across validation and pricing, keeping only the latest selection. */
	static getDefaultPrices(
		product: ProductStore,
		availableOptionTypes: StoreVariantOptionTypes,
		disabledOptionIds: Immutable.List<number>,
	): Immutable.OrderedMap<string, VariantPriceStore> {
		let productCache = this.defaultPrices.get(product);
		if (!productCache) {
			productCache = new WeakMap();
			this.defaultPrices.set(product, productCache);
		}

		const cached = productCache.get(availableOptionTypes);
		if (disabledOptionIds.count() === 0 && cached?.allPrices) return cached.allPrices;
		if (cached?.disabledOptionIds.equals(disabledOptionIds)) {
			return cached.prices;
		}

		const disabledOptionIdSet = new Set(disabledOptionIds.toArray());
		const enabledOptionTypes = Immutable.OrderedMap(availableOptionTypes).map(type => {
			return type.set('options', type.get('options').filter(option => !disabledOptionIdSet.has(option.get('id'))));
		});
		const prices = Immutable.OrderedMap(new VariantPriceHelper(product, enabledOptionTypes).buildVariantPrices());
		productCache.set(availableOptionTypes, { disabledOptionIds, prices, allPrices: disabledOptionIds.count() === 0 ? prices : cached?.allPrices });

		return prices;
	}

	static getAdjustedDefaultPrice(variantPrice: VariantPriceStore, product: ProductStore, listingPrice: PriceStore): PriceStore {
		const basePriceDelta = listingPrice.get('amount') - product.get('price_retail').get('amount');
		return basePriceDelta === 0 ? variantPrice.get('price') : variantPrice.get('price').set('amount',
			Math.max(0, variantPrice.get('price').get('amount') + basePriceDelta));
	}

	/** Resolve catalog defaults against the listing base; normalized overrides are final prices. */
	static resolvePrices(
		defaultPrices: Immutable.OrderedMap<string, VariantPriceStore>,
		variantPrices: StoreListingVariantPriceMap,
		product: ProductStore,
		listingPrice: PriceStore,
	): Immutable.OrderedMap<string, VariantPriceStore> {
		const prices = defaultPrices.map((variantPrice, id) => {
			return variantPrice.set('price', variantPrices.get(id)?.get('price')
				?? this.getAdjustedDefaultPrice(variantPrice, product, listingPrice));
		});

		return this.removeRedundantBasePrices(prices, product, listingPrice);
	}

	/**
	 * Removes variant prices that include at least one disabled product option.
	 */
	static filterEnabled<T extends StoreListingVariantPriceMap>(variantPrices: T, disabledOptionIds: Immutable.List<number>): T {
		if (disabledOptionIds.count() === 0) {
			return variantPrices;
		}

		const disabledOptionIdSet = new Set(disabledOptionIds.toArray());

		return variantPrices.filter(variantPrice => {
			return !variantPrice.get('option_ids').some(optionId => disabledOptionIdSet.has(optionId));
		}) as T;
	}

	/**
	 * Removes redundant variant prices when every combination has the base price and cost
	 * and all variant-specific dimensions contain price-neutral choices (e.g. notebook options).
	 * The product options remain available for publishing; only the redundant price overrides
	 * are removed.
	 */
	static removeRedundantBasePrices<T extends StoreListingVariantPriceMap>(
		variantPrices: T,
		product: ProductStore,
		listingPrice: PriceStore,
	): T {
		if (product.get('price_dropship').get('amount') <= 0 || variantPrices.count() === 0) {
			return variantPrices;
		}

		const prices = variantPrices.valueSeq().toArray();
		const productCost = product.get('price_dropship');

		if (!prices.every(variantPrice => {
			const price = variantPrice.get('price');
			const costPrice = variantPrice.get('costPrice');

			return price.get('amount') === listingPrice.get('amount')
				&& price.get('currency') === listingPrice.get('currency')
				&& costPrice.get('amount') === productCost.get('amount')
				&& costPrice.get('currency') === productCost.get('currency');
		})) {
			return variantPrices;
		}

		const optionsByType = new Map<number, Map<number, ProductOptionStore>>();
		prices.forEach(variantPrice => {
			variantPrice.get('options').forEach(option => {
				const typeId = option.get('id_product_option_type');
				const options = optionsByType.get(typeId) ?? new Map<number, ProductOptionStore>();

				options.set(option.get('id'), option);
				optionsByType.set(typeId, options);
			});
		});

		const allOptions = Array.from(optionsByType.values()).flatMap(options => Array.from(options.values()));
		const variantTypes = Array.from(optionsByType.values()).filter(options => {
			return Array.from(options.values()).some(option => option.get('id_product_variant') > 0);
		});

		const allOptionsArePriceNeutral = allOptions.every(option => {
			const quantityMultiplier = option.get('quantity_multiplier');

			return option.get('price_retail').get('amount') === 0
				&& option.get('price_dropship').get('amount') === 0
				&& option.get('listDependencies').count() === 0
				&& (quantityMultiplier === null || quantityMultiplier === 1);
		});

		return variantTypes.length > 0 && allOptionsArePriceNeutral
			? variantPrices.clear() as T
			: variantPrices;
	}
}
